import { getConfigValue } from './config';

const normalizeBaseUrl = (value) => value.replace(/\/+$/, '');

const getApiBase = () => {
  return normalizeBaseUrl(getConfigValue('REACT_APP_API_BASE', '/api'));
};

export const API_BASE = getApiBase();
export const MAPBOX_TOKEN =
  getConfigValue('REACT_APP_MAPBOX_TOKEN') || getConfigValue('REACT_APP_MAPBOX_PUBLIC_TOKEN');

const getMapboxTileUrl = () => {
  const template = getConfigValue('REACT_APP_MAPBOX_TILE_URL') || getConfigValue('REACT_APP_MAPBOX_STYLE_URL');
  const placeholder = '{MAPBOX_TOKEN}';

  if (!template) {
    return '';
  }

  if (template.includes(placeholder) && !MAPBOX_TOKEN) {
    return '';
  }

  return template.split(placeholder).join(encodeURIComponent(MAPBOX_TOKEN));
};

export const MAPBOX_TILE_URL = getMapboxTileUrl();
export const MAP_TILE_FALLBACK_URL = getConfigValue('REACT_APP_MAP_TILE_FALLBACK_URL');

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const parseStoredJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

export const getStoredDhtUserId = () => {
  const dhtUser = parseStoredJson('dhtUser') || {};
  const value = dhtUser.DHTUSR_id;
  return value === undefined || value === null || value === '' ? null : value;
};

export const withDhtUserId = (payload = {}) => {
  const cleanedPayload = Object.fromEntries(
    Object.entries(payload || {}).filter(([key]) => (
      !/_entry_?id$/i.test(key) &&
      !['entryId', 'entry_id'].includes(key)
    ))
  );
  const dhtUserId = getStoredDhtUserId();

  return dhtUserId === null
    ? cleanedPayload
    : { ...cleanedPayload, DHTUSR_id: dhtUserId };
};

const LANGUAGE_PREFERENCE_ID = 1;
const LANGUAGE_PREFERENCE_NAMES = ['taal', 'language'];

const getPreferenceId = (preference) => preference?.DHTPREF_id || preference?.id;

const requestJson = async (url, options = {}) => {
  const requestOptions = { ...options };

  if (requestOptions.body) {
    try {
      requestOptions.body = JSON.stringify(withDhtUserId(JSON.parse(requestOptions.body)));
    } catch {
      requestOptions.body = options.body;
    }
  }

  const response = await fetch(url, {
    ...requestOptions,
    headers: {
      Accept: '*/*',
      ...(requestOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...(requestOptions.headers || {})
    }
  });
  const responseData = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(responseData?.error || responseData?.message || `Request failed (${response.status})`);
  }

  return responseData;
};

export const fetchPreferences = () => requestJson(`${API_BASE}/preferences`);

export const createPreference = (payload) => requestJson(`${API_BASE}/preferences`, {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const fetchUserPreferences = () => requestJson(`${API_BASE}/user-preferences`);

export const createUserPreference = (payload) => requestJson(`${API_BASE}/user-preferences`, {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const fetchDhtTexts = async () => {
  const configuredEndpoint = getConfigValue('REACT_APP_DHT_TEXT_ENDPOINT');
  const endpoint = configuredEndpoint
    ? (configuredEndpoint.startsWith('http') ? configuredEndpoint : `${API_BASE}${configuredEndpoint.startsWith('/') ? '' : '/'}${configuredEndpoint}`)
    : `${API_BASE}/texts`;

  return requestJson(endpoint);
};

export const getOrCreateLanguagePreference = async () => {
  const preferences = await fetchPreferences();
  const languagePreference = Array.isArray(preferences)
    ? preferences.find((preference) => (
        Number(preference?.DHTPREF_id) === LANGUAGE_PREFERENCE_ID ||
        LANGUAGE_PREFERENCE_NAMES.includes(String(preference?.DHTPREF_name || '').trim().toLowerCase())
      ))
    : null;

  if (languagePreference) {
    return languagePreference;
  }

  return createPreference({
    DHTPREF_id: LANGUAGE_PREFERENCE_ID,
    DHTPREF_name: 'Taal',
    DHTPREF_description: 'Nederlands = 1, Engels = 2, Frans = 3, Duits = 4.'
  });
};

export const fetchUserLanguagePreference = async (userId) => {
  if (!userId) {
    return null;
  }

  const [languagePreference, userPreferences] = await Promise.all([
    getOrCreateLanguagePreference(),
    fetchUserPreferences()
  ]);
  const languagePreferenceId = getPreferenceId(languagePreference);

  if (!languagePreferenceId || !Array.isArray(userPreferences)) {
    return null;
  }

  const matchingPreferences = userPreferences
    .filter((preference) => (
      String(preference?.DHTAPPUSR_id) === String(userId) &&
      String(preference?.DHTPREF_id) === String(languagePreferenceId)
    ))
    .sort((a, b) => {
      const dateDiff = new Date(b?.DHTUSRPREF_entry_date || 0) - new Date(a?.DHTUSRPREF_entry_date || 0);
      if (dateDiff) return dateDiff;
      return Number(b?.id || b?.DHTUSRPREF_id || 0) - Number(a?.id || a?.DHTUSRPREF_id || 0);
    });

  return matchingPreferences[0]?.DHTPREF_value || null;
};

export const saveUserLanguagePreference = async ({ userId, languageValue }) => {
  if (!userId) {
    throw new Error('Missing user id for language preference.');
  }

  const languagePreference = await getOrCreateLanguagePreference();
  const languagePreferenceId = getPreferenceId(languagePreference);

  if (!languagePreferenceId) {
    throw new Error('Missing language preference id.');
  }

  return createUserPreference({
    DHTAPPUSR_id: Number(userId),
    DHTPREF_id: Number(languagePreferenceId),
    DHTPREF_value: String(languageValue)
  });
};

export const createHotel = async (payload) => {
  const response = await fetch(`${API_BASE}/hotels`, {
    method: 'POST',
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withDhtUserId(payload))
  });

  const responseData = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(
      responseData?.error ||
      responseData?.message ||
      `Failed to add hotel (${response.status})`
    );
  }

  return responseData;
};

export const addBox = async (payload) => {
  const response = await fetch(`${API_BASE}/add-box`, {
    method: 'POST',
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withDhtUserId(payload))
  });

  const responseData = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(
      responseData?.error ||
      responseData?.message ||
      `Failed to add box (${response.status})`
    );
  }

  return responseData;
};

export const fetchBoxStatusOptions = () => requestJson(`${API_BASE}/box-status`);

export const updateBoxStatus = (payload) => requestJson(`${API_BASE}/box-status`, {
  method: 'POST',
  body: JSON.stringify(payload)
});

const requestFirstAvailable = async (requests, fallbackMessage) => {
  let lastError = null;

  for (const request of requests) {
    let response;
    let responseData;

    try {
      response = await fetch(request.url, {
        method: request.method,
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(withDhtUserId(request.payload))
      });
      responseData = await parseJsonSafely(response);
    } catch (error) {
      lastError = error;
      continue;
    }

    if (response.ok) {
      return responseData;
    }

    const message = responseData?.error || responseData?.message || `${fallbackMessage} (${response.status})`;
    lastError = new Error(message);

    if (![404, 405].includes(response.status)) {
      throw lastError;
    }
  }

  throw lastError || new Error(fallbackMessage);
};

export const updateHotel = (hotelId, payload) => requestFirstAvailable([
  { url: `${API_BASE}/update-hotel`, method: 'POST', payload: { ...payload, DHTHOT_id: hotelId } }
], 'Failed to update hotel');

export const updateBox = (boxId, payload) => requestFirstAvailable([
  { url: `${API_BASE}/update-box`, method: 'POST', payload: { ...payload, DHTBOX_id: boxId } }
], 'Failed to update box');

export const updateComponent = (componentId, payload) => requestFirstAvailable([
  { url: `${API_BASE}/update-component`, method: 'POST', payload: { ...payload, DHTCMP_id: componentId } }
], 'Failed to update component');


export const fetchCommandsExecuted = async () => {
  const response = await fetch(`${API_BASE}/commands-executed`, {
    method: 'GET',
    headers: {
      Accept: '*/*'
    }
  });

  const responseData = await (async () => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  })();

  if (!response.ok) {
    throw new Error(
      responseData?.error ||
        responseData?.message ||
        `Failed to fetch commands executed (${response.status})`
    );
  }

  return responseData;
};

export const fetchCommandsPending = async () => {
  const response = await fetch(`${API_BASE}/commands-pending`, {
    method: 'GET',
    headers: {
      Accept: '*/*'
    }
  });

  const responseData = await (async () => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  })();

  if (!response.ok) {
    throw new Error(
      responseData?.error ||
        responseData?.message ||
        `Failed to fetch commands pending (${response.status})`
    );
  }

  return responseData;
};

export const sendBoxCommand = async ({ boxId, hotelBoxId, command, commandId }) => {
  const resolvedHotelBoxId = hotelBoxId || boxId;


  if (!resolvedHotelBoxId) {
    throw new Error('Missing hotel-box id for this box. Refresh the hotel data and try again.');
  }


  if (!commandId) {
    throw new Error('Missing command id for the selected command.');
  }

  const payload = {
    DHTHTBX_id: resolvedHotelBoxId,
    DHTCMD_id: commandId,
    DHTBOX_id: boxId,
    boxId,
    hotelBoxId: resolvedHotelBoxId,
    command,
    commandName: command,
    DHTCMD_name: command
  };


  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withDhtUserId(payload))
  };

  const endpoints = [
    `${API_BASE}/box-commands`,
    `${API_BASE}/commands`,
    `${API_BASE}/boxes/${boxId}/commands`
  ];

  let lastError = null;

  for (const endpoint of endpoints) {
    const response = await fetch(endpoint, requestOptions);
    const responseData = await parseJsonSafely(response);

    if (response.ok) {
      return responseData;
    }

    if (response.status === 404) {
      lastError = new Error(`Command endpoint not found at ${endpoint}`);
      continue;
    }

    throw new Error(responseData?.error || responseData?.message || 'Failed to send command');
  }

  throw lastError || new Error('Failed to send command');
};
